import { getJson, sendJson } from '@/lib/api';

/**
 * Structured templates: typed fields, an optional status list and a dated
 * log. Notes are the first consumer; the admin edits them under
 * Administration → Note templates.
 */

export const TEMPLATE_FIELD_TYPES = ['text', 'longtext', 'phone', 'datetime', 'url', 'bool', 'select', 'address', 'contact', 'list'] as const;
/** The types an item of a list field can have: everything but another list. */
export const ITEM_FIELD_TYPES = TEMPLATE_FIELD_TYPES.filter((type) => type !== 'list');
export type TemplateFieldType = (typeof TEMPLATE_FIELD_TYPES)[number];

/** Display labels for field types. */
export const TEMPLATE_FIELD_TYPE_LABELS: Readonly<Record<TemplateFieldType, string>> = {
  text: 'Short text',
  longtext: 'Paragraph',
  phone: 'Phone',
  datetime: 'Date & time',
  url: 'Link',
  bool: 'Yes / no',
  select: 'Choice',
  address: 'Address',
  contact: 'Contact',
  list: 'List of items',
};

export interface TemplateField {
  readonly key: string;
  readonly label: string;
  readonly type: TemplateFieldType;
  readonly options?: readonly string[];
  readonly required?: boolean;
  /** A line under the field saying what goes in it. */
  readonly hint?: string;
  /** For list fields: the shape of one item. Items cannot be lists. */
  readonly itemFields?: readonly TemplateField[];
}

export interface Template {
  readonly _id: string;
  readonly key: string;
  readonly name: string;
  readonly description?: string;
  /** Put into the note's subject when the template is chosen; editable after. */
  readonly defaultSubject?: string;
  readonly appliesTo: {
    readonly noteCategory?: string;
    readonly programTags?: readonly string[];
  };
  readonly status: { readonly enabled: boolean; readonly options: readonly string[] };
  readonly fields: readonly TemplateField[];
  readonly logEnabled: boolean;
  readonly enabled: boolean;
  readonly version: number;
  readonly usageCount: number;
  readonly updatedAt?: string;
}

/** A value a template field holds on a note. */
/** The parts of an address value, in the order they read. */
export const ADDRESS_PARTS = ['street', 'street2', 'city', 'state', 'postalCode'] as const;
/** The parts of a contact value, in the order they read. */
export const CONTACT_PARTS = ['name', 'company', 'email', 'phone'] as const;
export type ContactPart = (typeof CONTACT_PARTS)[number];

/**
 * Where a picked contact came from; mirrors `CONTACT_REF_KIND` in the API's
 * template-fields. The note keeps the parts as they were when picked, so it
 * reads the same after the contact changes or is removed.
 */
export const CONTACT_REF_KIND = {
  eventContact: 'event-contact',
  salesforceContact: 'salesforce-contact',
  staffContact: 'staff-contact',
  user: 'user',
} as const;
export type ContactRefKind = (typeof CONTACT_REF_KIND)[keyof typeof CONTACT_REF_KIND];
export interface ContactRef {
  readonly kind: ContactRefKind;
  readonly id: string;
}

export type TemplateAddress = Readonly<Partial<Record<(typeof ADDRESS_PARTS)[number], string>>>;
/** A contact's parts, the reference to who was picked, and `personId`, the older link to a team member. */
export type TemplateContact = Readonly<Partial<Record<ContactPart | 'personId', string>>> & { readonly contactRef?: ContactRef };
export type TemplateValue = string | number | boolean | null | TemplateAddress | TemplateContact | readonly TemplateListItem[];
/** One item of a list field: the item fields' values by key. */
export type TemplateListItem = Readonly<Record<string, string | number | boolean | null | TemplateAddress | TemplateContact>>;

/** Field types whose value is an object of named parts. */
export const COMPOSITE_FIELD_TYPES: ReadonlySet<TemplateFieldType> = new Set(['address', 'contact']);

/**
 * A composite value as one line: an address as "street, city, state postal",
 * a contact as "name · company · email · phone", skipping blank parts.
 */
export function formatCompositeValue(type: TemplateFieldType, value: unknown): string | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const parts = value as Readonly<Record<string, unknown>>;
  const part = (key: string): string => (typeof parts[key] === 'string' ? (parts[key] as string).trim() : '');
  if (type === 'address') {
    const cityState = [part('city'), part('state')].filter(Boolean).join(', ');
    return [part('street'), part('street2'), [cityState, part('postalCode')].filter(Boolean).join(' ')].filter(Boolean).join(', ') || undefined;
  }
  if (type === 'contact') return CONTACT_PARTS.map(part).filter(Boolean).join(' · ') || undefined;
  return undefined;
}
export type TemplateValues = Readonly<Record<string, TemplateValue>>;

/** Everything the API accepts when creating or editing a template. */
export type TemplateWrite = Omit<Template, '_id' | 'version' | 'usageCount' | 'updatedAt'>;

/** Every template, alphabetical. Seeds the built-in shapes on first read. */
export async function listTemplates(signal?: AbortSignal): Promise<readonly Template[]> {
  return getJson<Template[]>('/templates', undefined, signal);
}

/** Creates a template. */
export async function createTemplate(body: TemplateWrite): Promise<Template> {
  return sendJson<Template>('post', '/templates', body);
}

/** Updates a template; the key is fixed. */
export async function updateTemplate(id: string, body: Partial<Omit<TemplateWrite, 'key'>>): Promise<Template> {
  return sendJson<Template>('patch', `/templates/${id}`, body);
}

/** Deletes a template. Notes keep their values. */
export async function deleteTemplate(id: string): Promise<void> {
  await sendJson('delete', `/templates/${id}`);
}

/**
 * The template that offers itself for a note category, if any.
 *
 * @param templates - Every template.
 * @param category - The note category.
 * @returns The matching enabled template.
 */
export function templateForCategory(templates: readonly Template[], category: string): Template | undefined {
  return templates.find((template) => template.enabled && template.appliesTo.noteCategory === category);
}

/** A status pill tone from the status text, so "Confirmed" reads green anywhere. */
export function templateStatusTone(status: string): 'success' | 'danger' | 'warning' | 'neutral' {
  if (/confirmed|accepted|done|complete/i.test(status)) return 'success';
  if (/declined|cancel|failed|no response/i.test(status)) return 'danger';
  if (/pending|waiting|tentative/i.test(status)) return 'warning';
  return 'neutral';
}

/**
 * A field value as text for a card or list.
 *
 * @param field - The field definition, when known.
 * @param value - The stored value.
 * @returns The text, or undefined when blank.
 */
export function formatTemplateValue(field: TemplateField | undefined, value: TemplateValue | undefined): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (Array.isArray(value)) return value.length === 0 ? undefined : `${value.length} item${value.length === 1 ? '' : 's'}`;
  if (typeof value === 'object') return field ? formatCompositeValue(field.type, value) : formatCompositeValue('contact', value) ?? formatCompositeValue('address', value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (field?.type === 'datetime' && typeof value === 'string') {
    const ms = Date.parse(value);
    if (!Number.isNaN(ms)) {
      return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
  }
  return String(value);
}

const CONTACT_REF_KINDS: readonly string[] = Object.values(CONTACT_REF_KIND);

function isPlainRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isContactRefKind(value: unknown): value is ContactRefKind {
  return typeof value === 'string' && CONTACT_REF_KINDS.includes(value);
}

/**
 * Who a contact value points at: its reference, or the older `personId`
 * link read as a team member. Undefined when it was typed by hand.
 *
 * @param value - A stored contact value.
 * @returns The reference, or undefined.
 */
export function contactRefOf(value: unknown): ContactRef | undefined {
  if (!isPlainRecord(value)) return undefined;
  const record = value;
  const ref = record['contactRef'];
  if (typeof ref === 'object' && ref !== null && 'kind' in ref && 'id' in ref) {
    const { kind, id } = ref;
    if (isContactRefKind(kind) && typeof id === 'string' && id.length > 0) return { kind, id };
  }
  const legacy = record['personId'];
  return typeof legacy === 'string' && legacy.length > 0 ? { kind: CONTACT_REF_KIND.user, id: legacy } : undefined;
}

// ─── Where a template is used ───────────────────────────────────────────────

/** One note that follows a template, as the usage view lists it. */
export interface TemplateUsageNote {
  readonly noteId: string;
  readonly subject: string;
  readonly category: string;
  readonly status?: string;
  readonly updatedAt?: string;
  readonly author: { readonly id: string; readonly name: string } | null;
  readonly event: { readonly id: string; readonly name: string; readonly contractNumber?: string; readonly eventDate?: string };
}

export interface TemplateUsageView {
  readonly template: { readonly _id: string; readonly key: string; readonly name: string };
  /** Every live note that follows it. */
  readonly total: number;
  /** The most notes `notes` holds; the newest come first. */
  readonly limit: number;
  readonly notes: readonly TemplateUsageNote[];
}

/** The notes that follow a template, newest first. Admins only. */
export async function listTemplateUsage(id: string, signal?: AbortSignal): Promise<TemplateUsageView> {
  return getJson<TemplateUsageView>(`/templates/${id}/usage`, undefined, signal);
}
